import React from 'react';
import {
  Button,
  Input,
  Loader,
  MultiSelectDropdown,
  Popover,
  useToasts,
} from 'freemium-ui';
import uniq from 'lodash/uniq';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import EmptyState from 'shared/components/EmptyState';
import Icon from 'shared/components/Icon';
import { FormContainer, FormContent, FormHeader } from 'shared/styles';
import { AccountInfoType, ConfirmationType } from 'shared/types';
import { BlockTopicType, UpdateBlockedTopicsType } from './types';
import { useGetBlockedTopics, useUpdateBlockedTopics } from './hooks';
import { StyledBlockedTopic, StyledBlockedTopicPopover } from './styles';

type SentimentProps = {
  accountInfo?: AccountInfoType;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

export const BlockedSentimentTopic = ({
  accountInfo,
  setConfirmationData,
}: SentimentProps) => {
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const { setValue, watch } = useForm<BlockTopicType>({
    defaultValues: {
      blocked_keywords: [],
      created_by_id: '',
      updated_by_id: '',
      id: '',
    },
  });
  const {
    data: blockedTopics,
    isLoading: isTopicsLoading,
    refetch: refetchTopics,
  } = useGetBlockedTopics({});
  const { mutate: saveUpdatedBlockedTopics } = useUpdateBlockedTopics({
    setToast,
    callback: async (isSuccess: boolean, _response, updateType: string) => {
      const topicsToShow =
        updateType === 'add'
          ? blockedKeywords.join(', ')
          : `${deletedTopic.current?.slice(0, 100)}${
              (deletedTopic.current?.length ?? 0) > 100 ? '...' : ''
            }`;
      if (isSuccess) {
        setAnchorEl(null);
        setToast({
          type: 'success',
          delay: 3000,
          text: `${t(`settings.toasts.${updateType}_blocked_topic_success`, {
            topic: topicsToShow,
          })}`,
        });
        refetchTopics();
      } else {
        setToast({
          type: 'error',
          delay: 3000,
          text: `${t(`settings.toasts.${updateType}_blocked_topic_failed`, {
            topic: topicsToShow,
          })}`,
        });
      }
      deletedTopic.current = null;
    },
  });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLButtonElement>(
    null,
  );
  const deletedTopic = React.useRef<null | string>(null);
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  const blockedKeywords = watch('blocked_keywords');

  const setFormValue = (
    fieldId: keyof BlockTopicType,
    fieldValue: string | string[],
  ) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const updateBlockedTopics = (
    updatedKeywords: string[],
    updateType: UpdateBlockedTopicsType['updateType'],
  ) => {
    const isTopicExist = blockedTopics?.blocked_keywords !== null;

    saveUpdatedBlockedTopics({
      isTopicExist,
      updateType,
      payload: isTopicExist
        ? {
            blocked_keywords: updatedKeywords || [],
            created_by_id: blockedTopics?.created_by_id || '',
            updated_by_id: blockedTopics?.updated_by_id || '',
            id: blockedTopics?.id || '',
          }
        : {
            blocked_keywords: blockedKeywords || [],
            created_by_id: accountInfo?.created_by_id || '',
            updated_by_id: accountInfo?.created_by_id || '',
          },
    });
    setFormValue('blocked_keywords', []);
  };

  const onTopicDelete = (topicToDelete: string) => {
    deletedTopic.current = topicToDelete;
    updateBlockedTopics(
      blockedTopics?.blocked_keywords.filter(
        (each) => each !== topicToDelete,
      ) || [],
      'delete',
    );
  };

  const filteredBlockedTopics = React.useMemo(() => {
    const list = blockedTopics?.blocked_keywords?.filter((key) =>
      key.toLowerCase().match(searchTerm.toLowerCase().trim()),
    );
    return list;
  }, [searchTerm, blockedTopics?.blocked_keywords]);

  return (
    <StyledBlockedTopic>
      <FormContainer
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <FormHeader style={{ borderBottom: 'none' }}>
          <div className="left-pane">
            <div>
              <div className="heading">
                {t('settings.blocked_sentiment_topic.heading')}&nbsp;
                <span>
                  {blockedTopics?.blocked_keywords &&
                  blockedTopics?.blocked_keywords?.length > 0 ? (
                    <span>{`(${blockedTopics?.blocked_keywords.length})`}</span>
                  ) : null}
                </span>
              </div>
              <div className="sub-heading">
                {t('settings.blocked_sentiment_topic.description')}
              </div>
            </div>
          </div>
          <div className="right-pane">
            <Input
              value={searchTerm}
              className="search-input"
              startAdornment={
                <div className="search-icon">
                  <Icon glyph="search" width={18} height={18} />
                </div>
              }
              placeholder={`${t('forms.placeholders.search')}`}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
            />
            <Button
              id="add_topic-btn"
              inline
              aria-describedby="topic-popover"
              disabled={isTopicsLoading}
              onClick={(e) => setAnchorEl(e?.currentTarget)}
            >
              {t('settings.blocked_sentiment_topic.add_topic')}
            </Button>
            <Popover
              id="topic-popover"
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <StyledBlockedTopicPopover
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <div className="topic-header">
                  {t('responses.freddy_sentiment.add_keywords')}
                </div>
                <MultiSelectDropdown
                  name="blocked-topic"
                  InputProps={{
                    autoFocus: true,
                  }}
                  placeholder={
                    blockedKeywords && blockedKeywords?.length > 0
                      ? ''
                      : `${t('forms.placeholders.enter_topic_tags')}`
                  }
                  items={blockedKeywords || []}
                  selectedItem={(blockedKeywords || []).map((item) => ({
                    id: item,
                    topic: item,
                  }))}
                  onChange={(tags) =>
                    setFormValue(
                      'blocked_keywords',
                      tags.map(({ topic }) => topic?.trim()).filter(Boolean),
                    )
                  }
                  customValues
                  itemLabelIdentifier="topic"
                  itemValueIdentifier="id"
                />
                <div className="footer-buttons">
                  <Button
                    type="secondary"
                    onClick={() => {
                      setFormValue('blocked_keywords', []);
                      setAnchorEl(null);
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={() => {
                      const duplicateTopics = blockedTopics?.blocked_keywords
                        ? blockedKeywords?.filter((each) =>
                            blockedTopics?.blocked_keywords.includes(each),
                          )
                        : [];
                      if (duplicateTopics.length !== blockedKeywords.length) {
                        updateBlockedTopics(
                          uniq([
                            ...blockedKeywords,
                            ...(blockedTopics?.blocked_keywords ?? []),
                          ]),
                          'add',
                        );
                      } else {
                        setFormValue('blocked_keywords', []);
                      }

                      if (duplicateTopics.length) {
                        setToast({
                          type: 'error',
                          delay: 3000,
                          text: `${t(
                            'settings.toasts.duplicate_blocked_topic',
                            {
                              topics: `${duplicateTopics.join(', ')}`,
                            },
                          )}`,
                        });
                      }
                    }}
                    disabled={blockedKeywords.length < 1}
                  >
                    {t('add')}
                  </Button>
                </div>
              </StyledBlockedTopicPopover>
            </Popover>
          </div>
        </FormHeader>
        <FormContent pd="0 24px">
          {(blockedTopics?.blocked_keywords &&
            blockedTopics?.blocked_keywords.length < 1) ||
          blockedTopics?.blocked_keywords === null ? (
            <EmptyState
              title={`${t('empty_state.title.no_topics_to_display')}`}
              message={`${t('empty_state.message.click_to_add_topics')}`}
              imgSrc="empty-state-survey.svg"
              minHeight="400px"
            ></EmptyState>
          ) : (
            <>
              {isTopicsLoading ? (
                <div className="topic-loader">
                  <Loader />
                </div>
              ) : (
                <div className="blocked-keywords-container">
                  {blockedTopics?.blocked_keywords &&
                    filteredBlockedTopics?.map((b) => {
                      return (
                        <div className="blocked-keyword" key={b}>
                          {b}
                          <Icon
                            fill="#cfd7df"
                            glyph="cross"
                            height={10}
                            width={10}
                            className="delete-icon"
                            onClick={() => {
                              setConfirmationData({
                                id: new Date().toISOString(),
                                title: `${t(
                                  'settings.blocked_sentiment_topic.delete_confirmation_title',
                                )}`,
                                confirmButtonText: `${t('remove')}`,
                                cancelButtonText: t('cancel') || '',
                                onConfirm: () => {
                                  setConfirmationData({});
                                  onTopicDelete(b);
                                },

                                className: 'error',
                                description: `${t(
                                  'settings.blocked_sentiment_topic.delete_confirmation_desc',
                                )}`,
                                glyph: 'info',
                                infoText: `${t(
                                  'settings.blocked_sentiment_topic.delete_confirmation_infotext',
                                )}`,
                              });
                            }}
                          />
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          )}
        </FormContent>
      </FormContainer>
    </StyledBlockedTopic>
  );
};
